import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Brain, Activity, Heart, Loader2, User, Calendar, Phone, Mail, CheckCircle2, TrendingUp, Table2, Clock3 } from "lucide-react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { SubcategoryChart } from "@/components/patient/SubcategoryChart";
import { extractBloodPressureInfo } from "@/lib/groupLabResults";
import { RecommendationPanel } from "@/components/patient/RecommendationPanel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { PatientTimeline } from "@/components/patient/PatientTimeline";
import { getPatientPortalHealthAreaId, normalizeHealthAreaName } from "@/lib/patientExtensionHealthArea";
import { SystemDetailsModal } from "@/components/patient/SystemDetailsModal";
import logo from "@/assets/logo_header.png";

interface LabResultWithSubcategory {
  id: string;
  test_name: string;
  value: number | null;
  unit: string | null;
  ref_low: number | null;
  ref_high: number | null;
  collected_at: string;
  created_at: string;
  subcategory_id: string | null;
  lab_subcategories?: {
    id: string;
    name: string;
    lab_categories: {
      name: string;
    };
  };
}

interface SmartfileSession {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_insights: unknown;
  ai_summary: string | null;
  file_path: string | null;
  extracted_text: string | null;
  created_at: string;
  structured_records: unknown[] | null;
  patient_id: string | null;
  status: string | null;
}

interface HealthSystemDetailedMetric {
  name: string;
  value: string | number;
  unit?: string;
  status: "normal" | "abnormal";
  reference?: string;
}

interface HealthSystem {
  name: string;
  status: "excellent" | "good" | "attention" | "critical";
  metrics: number;
  alerts: number;
  summary?: string;
  detailed_metrics?: HealthSystemDetailedMetric[];
}

export default function SharedPatientView() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewerIsPatient, setViewerIsPatient] = useState(false);
  const [sharedClient, setSharedClient] = useState<SupabaseClient<Database> | null>(null);
  const [selectedSystem, setSelectedSystem] = useState<HealthSystem | null>(null);
  const [showSystemDetailsModal, setShowSystemDetailsModal] = useState(false);

  // Contador animado para score
  const count = useMotionValue(0);
  const rounded = useTransform(count, Math.round);

  useEffect(() => {
    loadSharedData();
  }, [token]);

  useEffect(() => {
    if (data?.analysis?.health_score) {
      animate(count, data.analysis.health_score, { duration: 1.5 });
    }
  }, [data?.analysis?.health_score]);

  const loadSharedData = async () => {
    try {
      const { data: authData } = await supabase.auth.getUser();
      if (authData.user?.id) {
        const patientHealthAreaId = await getPatientPortalHealthAreaId(supabase);
        const { data: viewerProfile } = await supabase
          .from("profiles")
          .select("health_area_id, health_area")
          .eq("id", authData.user.id)
          .maybeSingle();

        const normalizedViewerArea = normalizeHealthAreaName(viewerProfile?.health_area || "");
        const normalizedPatientArea = normalizeHealthAreaName("Paciente");
        const isPatientByText = normalizedViewerArea === normalizedPatientArea;
        const isPatientById =
          !!viewerProfile?.health_area_id &&
          !!patientHealthAreaId &&
          viewerProfile.health_area_id === patientHealthAreaId;

        setViewerIsPatient(isPatientByText || isPatientById);
      } else {
        setViewerIsPatient(false);
      }

      // Create a scoped client with the shared token header for RLS
      const tokenClient = createClient<Database>(
        import.meta.env.VITE_SUPABASE_URL,
        import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        {
          global: {
            headers: { "x-shared-token": token || "" },
          },
        }
      );
      setSharedClient(tokenClient);

      // Buscar link compartilhado
      const { data: linkData, error: linkError } = await tokenClient
        .from("shared_patient_links")
        .select("*, patients_onboarding(*)")
        .eq("token", token)
        .single();

      if (linkError) throw new Error("Link inválido ou expirado");

      // Verificar expiração
      if (linkData.expires_at && new Date(linkData.expires_at) < new Date()) {
        throw new Error("Este link expirou");
      }

      // Atualizar contador de visualizações via RPC segura
      await tokenClient.rpc("increment_shared_link_view_count", {
        link_token: token || "",
      });

      // Buscar dados públicos do profissional via RPC segura
      const { data: professionalData } = await tokenClient
        .rpc("get_shared_professional_public_info", {
          link_token: token || "",
        })
        .single();

      // Carregar dados do paciente
      const [analysisRes, labsRes, sessionsRes] = await Promise.all([
        tokenClient
          .from("ai_global_analysis_cache")
          .select("json_result")
          .eq("patient_id", linkData.patient_id)
          .maybeSingle(),

        tokenClient
          .from("lab_results_onboarding")
          .select(`
            *,
            lab_subcategories(
              id,
              name,
              lab_categories(name)
            )
          `)
          .eq("patient_id", linkData.patient_id)
          .not('subcategory_id', 'is', null)
          .order("collected_at", { ascending: true }),

        tokenClient
          .from("smartfile_sessions")
          .select("id, file_name, content_type, ai_insights, ai_summary, file_path, extracted_text, created_at, structured_records, patient_id, status")
          .eq("patient_id", linkData.patient_id)
          .not("structured_records", "is", null)
          .neq("content_type", "other")
          .order("created_at", { ascending: false })
      ]);

      // Agrupar resultados por subcategoria, detectando Pressão Arterial (Sistólica/Diastólica)
      // para renderizar como gráfico único combinado (mesmo padrão dos Indicadores de Saúde).
      const bpGroupKey = "__bp_pressao_arterial__";
      const groupedResults = (labsRes.data || []).reduce((acc: any[], result: LabResultWithSubcategory) => {
        if (!result.lab_subcategories) return acc;

        const { isBloodPressure, bpSide } = extractBloodPressureInfo(
          result.test_name || result.lab_subcategories.name
        );

        const enriched = {
          ...result,
          subcategory_name: result.lab_subcategories.name,
          category_name: result.lab_subcategories.lab_categories.name,
          ...(isBloodPressure && bpSide
            ? { _isBloodPressure: true, _bpSide: bpSide }
            : {}),
        };

        // Pressão arterial: agrupar Sistólica e Diastólica em um único card
        if (isBloodPressure) {
          let group = acc.find((g) => g.subcategory.id === bpGroupKey);
          if (!group) {
            group = {
              subcategory: {
                id: bpGroupKey,
                name: "Pressão Arterial",
                category_id: "",
                category_name: result.lab_subcategories.lab_categories.name,
              },
              results: [],
            };
            acc.push(group);
          }
          group.results.push(enriched);
          return acc;
        }

        const subcategoryId = result.lab_subcategories.id;
        let group = acc.find((g) => g.subcategory.id === subcategoryId);

        if (!group) {
          group = {
            subcategory: {
              id: result.lab_subcategories.id,
              name: result.lab_subcategories.name,
              category_id: "",
              category_name: result.lab_subcategories.lab_categories.name,
            },
            results: [],
          };
          acc.push(group);
        }

        group.results.push(enriched);

        return acc;
      }, []);

      setData({
        patient: linkData.patients_onboarding,
        link: linkData,
        professional: professionalData,
        analysis: analysisRes.data?.json_result,
        groupedResults,
        smartfileSessions: (sessionsRes.data || []) as SmartfileSession[],
      });

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (birthDate: string | null) => {
    if (!birthDate) return "N/A";
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getLatestResult = (group: any): LabResultWithSubcategory | null => {
    const results: LabResultWithSubcategory[] = group?.results || [];
    if (results.length === 0) return null;
    return results[results.length - 1];
  };

  const getResultStatus = (result: LabResultWithSubcategory | null): "normal" | "baixo" | "alto" | "sem_faixa" => {
    if (!result || result.value === null) return "sem_faixa";
    if (result.ref_low !== null && result.value < result.ref_low) return "baixo";
    if (result.ref_high !== null && result.value > result.ref_high) return "alto";
    if (result.ref_low !== null || result.ref_high !== null) return "normal";
    return "sem_faixa";
  };

  const formatReferenceRange = (result: LabResultWithSubcategory | null) => {
    if (!result) return "N/A";
    const low = result.ref_low !== null ? result.ref_low : "—";
    const high = result.ref_high !== null ? result.ref_high : "—";
    const unit = result.unit ? ` ${result.unit}` : "";
    return `${low} - ${high}${unit}`;
  };

  const getStatusLabel = (status: "normal" | "baixo" | "alto" | "sem_faixa") => {
    if (status === "normal") return "Normal";
    if (status === "baixo") return "Baixo";
    if (status === "alto") return "Alto";
    return "Sem faixa";
  };

  const handleSystemClick = (system: HealthSystem) => {
    setSelectedSystem(system);
    setShowSystemDetailsModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-purple-600/5">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Carregando informações...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="text-red-600 text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-2">Link Inválido</h2>
            <p className="text-muted-foreground">{error || "Este link não existe ou expirou"}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const analysis = data.analysis;

  // Blocos visíveis com defaults
  const defaultBlocks = {
    patient_data: true,
    health_score: true,
    indicators: true,
    analytical_report: true,
    timeline: true,
    insights: true,
    health_map: true,
    analysis: true,
    recommendations: true,
    highlights: true,
  };
  const visibleBlocks = {
    ...defaultBlocks,
    ...(data.link.visible_blocks || {}),
  };
  const shouldShowProfessionalHeader = data.professional && !viewerIsPatient;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-purple-600/5 to-blue-600/5">
      {/* Header Sticky com Backdrop Blur */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-background/80 backdrop-blur-lg border-b sticky top-0 z-50 shadow-sm"
      >
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-3">
            {/* Lado Esquerdo: Ícone + Título */}
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="size-9 sm:size-10 bg-gradient-to-br from-primary to-purple-600 rounded-full flex items-center justify-center shadow-sm shrink-0">
                <Heart className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
              <h1 className="text-sm sm:text-base font-semibold leading-tight bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent truncate">
                Relatório Detalhado Saúde
              </h1>
            </div>

            {/* Lado Direito: Logo + Profissional */}
            <div className="flex items-center gap-3 shrink-0">
              {shouldShowProfessionalHeader && (
                <div className="flex items-center gap-2.5">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium leading-tight">{data.professional.full_name}</p>
                    {data.professional.health_area && (
                      <p className="text-xs text-muted-foreground">{data.professional.health_area}</p>
                    )}
                  </div>
                  <Avatar className="size-9 border border-primary/20 hidden sm:block">
                    {data.professional.avatar_url && <AvatarImage src={data.professional.avatar_url} />}
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                      {data.professional.full_name?.charAt(0)?.toUpperCase() || "P"}
                    </AvatarFallback>
                  </Avatar>
                </div>
              )}
              <img src={logo} alt="Elinia" className="h-6 sm:h-7 w-auto object-contain" />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Seção Dados do Profissional - Visível apenas no Mobile */}
      {shouldShowProfessionalHeader && (
        <div className="container mx-auto px-4 pt-6 sm:hidden max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <Card className="bg-gradient-to-br from-primary/5 to-purple-600/5 border-primary/20">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16 border-2 border-primary/30">
                    {data.professional.avatar_url && (
                      <AvatarImage src={data.professional.avatar_url} />
                    )}
                    <AvatarFallback className="bg-primary/20 text-primary font-bold text-xl">
                      {data.professional.full_name?.charAt(0)?.toUpperCase() || "P"}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1">
                    <p className="font-semibold text-lg text-foreground">
                      {data.professional.full_name}
                    </p>
                    {data.professional.health_area && (
                      <p className="text-sm text-muted-foreground">
                        {data.professional.health_area}
                      </p>
                    )}
                    <Badge variant="secondary" className="mt-1 text-xs">
                      Responsável pelo relatório
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Conteúdo Principal */}
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="space-y-8">

          {/* SEÇÃO 1: Dados Cadastrais */}
          {visibleBlocks.patient_data && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5 text-primary" />
                    Dados do Paciente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Nome Completo</p>
                      <p className="font-semibold">{data.patient.full_name}</p>
                    </div>

                    {data.patient.data_nascimento && (
                      <>
                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Idade
                          </p>
                          <p className="font-semibold">{calculateAge(data.patient.data_nascimento)} anos</p>
                        </div>

                        <div className="space-y-1">
                          <p className="text-xs text-muted-foreground">Data de Nascimento</p>
                          <p className="font-semibold">
                            {new Date(data.patient.data_nascimento).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </>
                    )}

                    {data.patient.sexo && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Sexo</p>
                        <p className="font-semibold">
                          {data.patient.sexo === 'M' ? 'Masculino' : data.patient.sexo === 'F' ? 'Feminino' : data.patient.sexo}
                        </p>
                      </div>
                    )}

                    {data.patient.cpf && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">CPF</p>
                        <p className="font-semibold font-mono">{data.patient.cpf}</p>
                      </div>
                    )}

                    {data.patient.email && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          E-mail
                        </p>
                        <p className="font-semibold text-sm">{data.patient.email}</p>
                      </div>
                    )}

                    {data.patient.celular && (
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          Celular
                        </p>
                        <p className="font-semibold">{data.patient.celular}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* SEÇÃO 2: Score de Saúde com Contador Animado */}
          {visibleBlocks.health_score && analysis?.health_score && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="border-2 border-primary/20 shadow-2xl relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-purple-600/10" />
                <CardContent className="pt-8 pb-6 relative z-10">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="text-center md:text-left">
                      <h2 className="text-3xl font-bold mb-2">Score de Saúde</h2>
                      <p className="text-muted-foreground">
                        Baseado em {analysis?.stats?.records || 0} registros analisados
                      </p>
                    </div>
                    <motion.div
                      className="text-center"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.5, type: "spring" }}
                    >
                      <motion.div className="text-7xl font-bold text-primary">
                        {rounded}
                      </motion.div>
                      <span className="text-2xl text-muted-foreground">/100</span>
                    </motion.div>
                  </div>
                  <Progress value={analysis.health_score} className="h-4 mt-6" />
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* SEÇÃO 3: Indicadores de Saúde com Gráficos */}
          {visibleBlocks.indicators && data.groupedResults && data.groupedResults.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Indicadores de Saúde
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {data.groupedResults.map((group: any, i: number) => (
                      <motion.div
                        key={group.subcategory.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4 + i * 0.05 }}
                      >
                        <SubcategoryChart
                          subcategory={group.subcategory}
                          results={group.results}
                          period="all"
                        />
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* SEÇÃO 3.1: Relatório Analítico (Tabela) */}
          {visibleBlocks.analytical_report && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Table2 className="h-5 w-5 text-primary" />
                    Relatório Analítico
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!data.groupedResults || data.groupedResults.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Nenhum resultado disponível para exibir na tabela do relatório analítico.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      <div className="md:hidden divide-y rounded-lg border bg-card overflow-hidden">
                        <div className="grid grid-cols-2 gap-3 px-4 py-3 bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          <span>Indicador</span>
                          <span>Categoria</span>
                        </div>
                        {data.groupedResults.map((group: any) => {
                          const latest = getLatestResult(group);
                          const status = getResultStatus(latest);
                          const statusStyles: Record<string, string> = {
                            normal: "text-green-700 bg-green-50 border-green-200",
                            baixo: "text-amber-700 bg-amber-50 border-amber-200",
                            alto: "text-red-700 bg-red-50 border-red-200",
                            sem_faixa: "text-slate-700 bg-slate-50 border-slate-200",
                          };

                          return (
                            <div key={group.subcategory.id} className="px-4 py-3 space-y-2.5">
                              <div className="grid grid-cols-2 gap-3 items-center">
                                <p className="font-semibold text-sm text-primary">{group.subcategory.name}</p>
                                <p className="text-sm text-muted-foreground">{group.subcategory.category_name}</p>
                              </div>
                              <div className="rounded-md bg-muted/30 px-3 py-1 text-xs divide-y divide-border/50">
                                <div className="flex items-center justify-between py-2">
                                  <span className="text-muted-foreground">Último valor</span>
                                  <span className="font-medium text-foreground">
                                    {latest?.value !== null && latest?.value !== undefined
                                      ? `${latest.value}${latest.unit ? ` ${latest.unit}` : ""}`
                                      : "N/A"}
                                  </span>
                                </div>
                                <div className="flex items-center justify-between py-2">
                                  <span className="text-muted-foreground">Faixa de referência</span>
                                  <span className="font-medium text-foreground">{formatReferenceRange(latest)}</span>
                                </div>
                                <div className="flex items-center justify-between py-2">
                                  <span className="text-muted-foreground">Data da coleta</span>
                                  <span className="font-medium text-foreground">
                                    {latest?.collected_at
                                      ? new Date(latest.collected_at).toLocaleDateString("pt-BR")
                                      : "N/A"}
                                  </span>
                                </div>
                              </div>
                              <div className="flex justify-end">
                                <Badge variant="outline" className={statusStyles[status]}>
                                  {getStatusLabel(status)}
                                </Badge>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full min-w-[760px] text-sm">
                          <thead>
                            <tr className="border-b bg-muted/40">
                              <th className="text-left font-semibold p-3">Indicador</th>
                              <th className="text-left font-semibold p-3">Categoria</th>
                              <th className="text-left font-semibold p-3">Último valor</th>
                              <th className="text-left font-semibold p-3">Faixa de referência</th>
                              <th className="text-left font-semibold p-3">Data da coleta</th>
                              <th className="text-left font-semibold p-3">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.groupedResults.map((group: any) => {
                              const latest = getLatestResult(group);
                              const status = getResultStatus(latest);
                              const statusStyles: Record<string, string> = {
                                normal: "text-green-700 bg-green-50 border-green-200",
                                baixo: "text-amber-700 bg-amber-50 border-amber-200",
                                alto: "text-red-700 bg-red-50 border-red-200",
                                sem_faixa: "text-slate-700 bg-slate-50 border-slate-200",
                              };

                              return (
                                <tr key={group.subcategory.id} className="border-b last:border-0">
                                  <td className="p-3 font-medium">{group.subcategory.name}</td>
                                  <td className="p-3 text-muted-foreground">{group.subcategory.category_name}</td>
                                  <td className="p-3">
                                    {latest?.value !== null && latest?.value !== undefined
                                      ? `${latest.value}${latest.unit ? ` ${latest.unit}` : ""}`
                                      : "N/A"}
                                  </td>
                                  <td className="p-3 text-muted-foreground">{formatReferenceRange(latest)}</td>
                                  <td className="p-3 text-muted-foreground">
                                    {latest?.collected_at
                                      ? new Date(latest.collected_at).toLocaleDateString("pt-BR")
                                      : "N/A"}
                                  </td>
                                  <td className="p-3">
                                    <Badge
                                      variant="outline"
                                      className={statusStyles[status]}
                                    >
                                      {getStatusLabel(status)}
                                    </Badge>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* SEÇÃO 3.2: Timeline */}
          {visibleBlocks.timeline && data.smartfileSessions && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock3 className="h-5 w-5 text-primary" />
                    Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <PatientTimeline
                    patientId={data.patient.id}
                    sessions={data.smartfileSessions}
                    supabaseClient={sharedClient ?? undefined}
                  />
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* SEÇÃO 4: Insights e Alertas */}
          {visibleBlocks.insights && analysis?.highlights && analysis.highlights.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-primary" />
                    Insights e Alertas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {analysis.highlights.map((highlight: any, i: number) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 + i * 0.05 }}
                        whileHover={{ scale: 1.02 }}
                      >
                        <Card className={`border-l-4 ${highlight.type === "warning" ? "border-l-amber-500 bg-amber-50/50" :
                          highlight.type === "success" ? "border-l-green-500 bg-green-50/50" :
                            "border-l-blue-500 bg-blue-50/50"
                          } hover:shadow-lg transition-all`}>
                          <CardContent className="pt-4">
                            <h3 className="font-semibold mb-2 flex items-center gap-2">
                              <Activity className="h-4 w-4" />
                              {highlight.title}
                            </h3>
                            <p className="text-sm text-muted-foreground">{highlight.message}</p>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* SEÇÃO 5: Mapa de Sistemas de Saúde Expandido */}
          {visibleBlocks.health_map && analysis?.systems && analysis.systems.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Brain className="h-5 w-5 text-primary" />
                    Mapa de Sistemas de Saúde
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {analysis.systems.map((sys: HealthSystem, i: number) => {
                      const statusColor =
                        sys.status === "excellent" ? "border-t-green-500" :
                          sys.status === "good" ? "border-t-blue-500" :
                            sys.status === "attention" ? "border-t-amber-500" :
                              "border-t-red-500";

                      const hasAlert = sys.alerts > 0;

                      return (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.8 + i * 0.05 }}
                          whileHover={{ scale: 1.03 }}
                        >
                          <Card
                            className={`border-t-4 ${statusColor} hover:shadow-lg transition-all ${hasAlert ? "animate-pulse cursor-pointer" : ""}`}
                            onClick={() => handleSystemClick(sys)}
                          >
                            <CardContent className="pt-4">
                              <h4 className="font-semibold capitalize mb-3">{sys.name}</h4>

                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between items-center">
                                  <span className="text-muted-foreground">Métricas</span>
                                  <span className="font-semibold">{sys.metrics}</span>
                                </div>

                                <div className="flex justify-between items-center">
                                  <span className="text-muted-foreground">Status</span>
                                  <Badge variant={
                                    sys.status === "excellent" ? "default" :
                                      sys.status === "good" ? "secondary" :
                                        sys.status === "attention" ? "outline" :
                                          "destructive"
                                  }>
                                    {sys.status === "excellent" ? "Excelente" :
                                      sys.status === "good" ? "Bom" :
                                        sys.status === "attention" ? "Atenção" :
                                          "Crítico"}
                                  </Badge>
                                </div>

                                {hasAlert && (
                                  <Badge
                                    variant="destructive"
                                    className="w-full justify-center mt-2 cursor-pointer"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleSystemClick(sys);
                                    }}
                                  >
                                    {sys.alerts} alerta{sys.alerts > 1 ? 's' : ''} ativo{sys.alerts > 1 ? 's' : ''}
                                  </Badge>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* SEÇÃO 6: Análise Global da IA */}
          {analysis && (visibleBlocks.analysis || visibleBlocks.recommendations || visibleBlocks.highlights) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="space-y-6"
            >
              {/* Resumo Clínico */}
              {visibleBlocks.analysis && analysis.summary && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Brain className="h-5 w-5 text-primary" />
                      Análise Clínica Inteligente
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                      {analysis.summary}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Recomendações Priorizadas */}
              {visibleBlocks.recommendations && analysis.recommendations && analysis.recommendations.length > 0 && (
                <RecommendationPanel recommendations={analysis.recommendations} />
              )}

              {/* Destaques Clínicos */}
              {visibleBlocks.highlights && analysis.clinical_highlights && analysis.clinical_highlights.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      Destaques Clínicos
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {analysis.clinical_highlights.map((highlight: string, i: number) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1 + i * 0.05 }}
                          className="flex items-start gap-3"
                        >
                          <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                          <span className="text-sm text-muted-foreground">{highlight}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
            className="text-center text-sm text-muted-foreground pt-8 border-t space-y-2"
          >
            <p>🔒 Relatório confidencial</p>
            <p>Este link expira automaticamente em 30 dias</p>
            <p className="text-xs">Visualizado {data.analysis?.view_count || 0} vez(es)</p>
          </motion.div>
        </div>
      </div>

      {selectedSystem && (
        <SystemDetailsModal
          open={showSystemDetailsModal}
          onClose={() => {
            setShowSystemDetailsModal(false);
            setSelectedSystem(null);
          }}
          system={selectedSystem}
        />
      )}
    </div>
  );
}
